import type { SlaSnapshot } from "@helpdesk/sla";

import { cn } from "./ui/cn";
import { formatRemaining, slaTextClass } from "../lib/sla";

/**
 * The SLA risk indicator: a clock face that fills as the allowance is consumed.
 *
 * Colour is the primary signal (green over half remaining, amber under half,
 * red overdue) but never the *only* one -- the hand position and the countdown
 * both carry the same information, because roughly one agent in twelve cannot
 * reliably separate the green from the red.
 */

const SIZES = {
  sm: { box: 16, stroke: 2 },
  md: { box: 20, stroke: 2.25 },
} as const;

interface SlaClockProps {
  sla: SlaSnapshot;
  size?: keyof typeof SIZES;
  className?: string;
}

/**
 * What to add to the tooltip when the countdown is frozen.
 *
 * The two reasons want different words. "Outside business hours" is the desk's
 * own schedule and every clock on screen says it at once, so a reader who sees
 * it on one ticket has already explained the rest. "The assignee is away" is
 * the opposite -- it explains why *this* clock is still while the one beside
 * it ticks, which is the question that otherwise gets asked out loud.
 */
function pauseNote(sla: SlaSnapshot): string {
  if (!sla.paused) return "";
  if (sla.pausedReason === "away") return " (paused — the assignee is away)";
  return " (paused — outside business hours)";
}

export function SlaClock({ sla, size = "sm", className }: SlaClockProps) {
  const { box, stroke } = SIZES[size];
  const radius = (box - stroke) / 2;
  const centre = box / 2;

  // Nothing being measured: an untimed pipeline, or no unanswered message.
  if (sla.level === null) {
    return (
      <span
        className={cn("inline-flex items-center text-faint", className)}
        title={
          sla.running
            ? "No SLA target for this pipeline"
            : "No response outstanding"
        }
        aria-label="No SLA"
      >
        <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`} aria-hidden>
          <circle
            cx={centre}
            cy={centre}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeDasharray="2 2"
            opacity={0.6}
          />
        </svg>
      </span>
    );
  }

  const circumference = 2 * Math.PI * radius;
  const consumed = sla.consumed ?? 0;
  const colour = slaTextClass(sla.level);

  const label =
    sla.level === "breached"
      ? `SLA breached by ${formatRemaining(sla.remainingMinutes).replace(" over", "")}`
      : `${formatRemaining(sla.remainingMinutes)} of SLA remaining`;

  return (
    <span
      className={cn("inline-flex items-center", colour, className)}
      title={`${label}${pauseNote(sla)}`}
      aria-label={label}
    >
      <svg width={box} height={box} viewBox={`0 0 ${box} ${box}`} aria-hidden>
        {/* Track */}
        <circle
          cx={centre}
          cy={centre}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          opacity={0.2}
        />
        {/* Consumed arc, drawn clockwise from 12 o'clock. */}
        <circle
          cx={centre}
          cy={centre}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - consumed)}
          transform={`rotate(-90 ${centre} ${centre})`}
          /*
           * No CSS transition: this redraws every second, and a 1s ease would
           * mean the hand is always animating towards a value it never reaches.
           */
        />
        {/*
         * A breached clock gets a filled centre as a non-colour cue, so the
         * distinction survives greyscale and colour-blindness.
         */}
        {sla.level === "breached" ? (
          <circle cx={centre} cy={centre} r={radius * 0.4} fill="currentColor" />
        ) : null}
      </svg>
    </span>
  );
}

/** Clock plus the countdown text, as used in list rows and the ticket header. */
export function SlaIndicator({
  sla,
  size = "sm",
  className,
}: SlaClockProps) {
  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <SlaClock sla={sla} size={size} />
      <span
        className={cn(
          /* `whitespace-nowrap` because this sits in list columns narrow
             enough to break "2h 30m" across two lines, and a row whose height
             depends on how late it is destroys the rhythm of the list it is
             in. Better to overflow a track than to wrap inside one. */
          "whitespace-nowrap tabular-nums text-xs font-medium",
          slaTextClass(sla.level),
          // A frozen clock is dimmed so nobody reads a paused number as live.
          sla.paused && "opacity-60",
        )}
      >
        {formatRemaining(sla.remainingMinutes)}
      </span>
    </span>
  );
}

/**
 * The SLA as the phone's ticket bar says it: the clock, and the time left in
 * words -- "2h 15m left", or "45m over" once it has gone.
 *
 * Its own wording rather than `SlaIndicator`'s bare countdown, because on the
 * bar it sits beside the assignee and Close with no column heading to say what
 * the number is.
 */
export function SlaLeft({ sla, className }: { sla: SlaSnapshot; className?: string }) {
  if (sla.level === null) return null;
  const text =
    sla.level === "breached"
      ? `${formatRemaining(sla.remainingMinutes).replace(" over", "")} over`
      : `${formatRemaining(sla.remainingMinutes)} left`;
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <SlaClock sla={sla} size="md" />
      <span
        className={cn(
          "truncate whitespace-nowrap text-sm font-medium tabular-nums",
          slaTextClass(sla.level),
          sla.paused && "opacity-60",
        )}
      >
        {text}
        {sla.paused ? " · paused" : ""}
      </span>
    </span>
  );
}
